import React, { useRef, useEffect, useState } from 'react';
import { Location } from '../types';

interface LocationInputProps {
  value: string;
  onChange: (value: string) => void;
  onLocationSelect: (location: Location, address: string) => void;
  placeholder: string;
  icon: string;
  iconColor: string;
  isStart?: boolean;
  disabled?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
}

export const LocationInput: React.FC<LocationInputProps> = ({
  value,
  onChange,
  onLocationSelect,
  placeholder,
  icon,
  iconColor,
  isStart: _isStart = false,
  disabled = false,
  onFocus,
  onBlur,
  inputRef: externalInputRef,
}) => {
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef || internalInputRef;
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const isSelectingFromAutocomplete = useRef(false);
  const blurTimeoutRef = useRef<number | null>(null);
  const isGeocodingRef = useRef(false);
  const isClickingAutocompleteRef = useRef(false);

  // Sync input value when value prop changes from outside (but not during autocomplete selection or typing)
  useEffect(() => {
    // Don't sync if user is selecting from autocomplete dropdown
    // But allow syncing when focused if the value prop changed from outside (e.g., from parent)
    if (isSelectingFromAutocomplete.current) {
      return;
    }

    // Sync the input value with the prop value
    // This ensures the input reflects the current value prop
    if (inputRef.current && inputRef.current.value !== value) {
      inputRef.current.value = value;
    }
  }, [value]);

  useEffect(() => {
    if (!inputRef.current || !window.google?.maps?.places) return;

    // Create Autocomplete instance
    const autocomplete = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        fields: [
          'formatted_address',
          'address_components',
          'geometry',
          'name',
          'vicinity',
          'place_id'
        ],
        types: ['geocode', 'establishment'],
      }
    );

    autocompleteRef.current = autocomplete;

    // Listen for clicks on autocomplete dropdown items
    // This helps detect when user is clicking on a suggestion
    const handleDocumentMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if click is on an autocomplete suggestion (Google Maps adds specific classes)
      if (
        target &&
        (target.classList.contains('pac-item') ||
          target.closest('.pac-item') ||
          target.classList.contains('pac-container') ||
          target.closest('.pac-container'))
      ) {
        isClickingAutocompleteRef.current = true;
        console.log('Detected click on autocomplete suggestion');
      }
    };

    // Add mousedown listener to document to detect clicks on autocomplete suggestions
    document.addEventListener('mousedown', handleDocumentMouseDown);

    // Handle place selection (works for both keyboard and mouse)
    const handlePlaceSelect = () => {
      // Cancel any pending blur
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      // Set flag to prevent React from overriding the value and prevent geocoding
      isSelectingFromAutocomplete.current = true;

      console.log('place_changed event fired');

      const place = autocomplete.getPlace();
      console.log('Place from autocomplete:', place);

      // Check if place was actually selected (has place_id or geometry)
      if (!place.place_id && !place.geometry?.location) {
        // Place selection incomplete, reset flag
        isSelectingFromAutocomplete.current = false;
        return;
      }

      if (!place.geometry?.location) {
        console.warn('Place selection missing geometry', place);
        isSelectingFromAutocomplete.current = false;
        return;
      }

      const location: Location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };

      // Get the full formatted address - prioritize formatted_address
      let address = '';

      // First priority: formatted_address (full address)
      if (place.formatted_address) {
        address = place.formatted_address;
      }
      // Second priority: name + vicinity for establishments
      else if (place.name && place.vicinity) {
        address = `${place.name}, ${place.vicinity}`;
      }
      // Third priority: just name
      else if (place.name) {
        address = place.name;
      }
      // Fourth priority: check what Google set in the input field
      else if (inputRef.current?.value) {
        address = inputRef.current.value;
      }
      // Last resort: current prop value
      else {
        address = value || '';
      }

      console.log('Place selected:', { address, location, place });

      // Cancel any pending blur timeout
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      // Immediately set the input value first (before React state update)
      // This ensures the input shows the selected address immediately
      if (inputRef.current) {
        inputRef.current.value = address;
      }

      // Update parent state - this will update the value prop
      onChange(address);

      // Trigger location select IMMEDIATELY and SYNCHRONOUSLY
      // This is the "enter" action - it confirms and sets the location
      // No waiting, no delays - immediate execution
      try {
        onLocationSelect(location, address);
        console.log('Location select called immediately:', { location, address });
      } catch (error) {
        console.error('Error calling onLocationSelect:', error);
      }

      // Reset flags immediately
      isSelectingFromAutocomplete.current = false;
      isClickingAutocompleteRef.current = false;

      // Immediately blur the input to complete the selection (like pressing Enter)
      // Don't wait for requestAnimationFrame - do it synchronously
      setIsFocused(false);
      if (inputRef.current) {
        // Use setTimeout with 0 delay to ensure it happens after the current call stack
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.blur();
          }
          onBlur?.();
        }, 0);
      } else {
        onBlur?.();
      }
    };

    // Listen for place_changed event (fired when place is selected via keyboard or mouse)
    const listener = autocomplete.addListener('place_changed', handlePlaceSelect);

    return () => {
      // Remove autocomplete listener
      if (listener) {
        window.google.maps.event.removeListener(listener);
      }

      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }

      // Remove document mousedown listener
      document.removeEventListener('mousedown', handleDocumentMouseDown);

      // Clear any pending timeouts
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    };
  }, [onChange, onLocationSelect, value]);

  // Handle geocoding when user presses Enter without selecting from autocomplete
  const handleGeocodeAddress = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!window.google?.maps?.Geocoder) return;

    handleGeocodeAddress.current = () => {
      // Don't geocode if already processing or if selecting from autocomplete
      if (isGeocodingRef.current || isSelectingFromAutocomplete.current) {
        return;
      }

      const address = inputRef.current?.value?.trim();
      if (!address || address.length === 0) {
        return;
      }

      isGeocodingRef.current = true;

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        isGeocodingRef.current = false;

        if (status === 'OK' && results && results.length > 0) {
          const result = results[0];
          const location: Location = {
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng(),
          };

          const formattedAddress = result.formatted_address || address;

          // Update parent state
          onChange(formattedAddress);

          // Set input value
          if (inputRef.current) {
            inputRef.current.value = formattedAddress;
          }

          // Trigger location select
          onLocationSelect(location, formattedAddress);

          // Blur after a short delay
          setTimeout(() => {
            setIsFocused(false);
            if (inputRef.current) {
              inputRef.current.blur();
            }
            onBlur?.();
          }, 100);
        } else {
          console.warn('Geocoding failed:', status);
          // Keep the address as-is but don't update location
        }
      });
    };
  }, [onChange, onLocationSelect, onBlur]);

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const location: Location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        // Reverse geocode to get address
        if (window.google?.maps?.Geocoder) {
          try {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location }, (results, status) => {
              if (status === 'OK' && results && results.length > 0) {
                const address = results[0].formatted_address;
                onChange(address);
                onLocationSelect(location, address);
              } else {
                // Fallback to coordinates
                const address = `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
                onChange(address);
                onLocationSelect(location, address);
              }
            });
          } catch (error) {
            console.error('Geocoding error:', error);
            const address = `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
            onChange(address);
            onLocationSelect(location, address);
          }
        } else {
          // Fallback to coordinates if geocoder not available
          const address = `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
          onChange(address);
          onLocationSelect(location, address);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Unable to retrieve your location');
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 60000,
      }
    );
  };

  return (
    <div className="relative">
      <div
        className={`flex items-center bg-white rounded-lg border transition-all ${
          isFocused ? 'border-blue-500 shadow-md' : 'border-gray-200'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {/* Icon */}
        <div className="flex items-center pl-3 pr-2">
          <span
            className={`material-symbols-outlined text-[20px]`}
            style={{ color: iconColor }}
          >
            {icon}
          </span>
        </div>

        {/* Input - controlled input that works with Google Autocomplete */}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => {
            // If disabled, prevent changes
            if (disabled) {
              e.preventDefault();
              return;
            }
            // Allow typing to work normally, but not during autocomplete selection
            if (!isSelectingFromAutocomplete.current) {
              onChange(e.target.value);
            }
          }}
          onKeyDown={(e) => {
            // If disabled, prevent all keyboard input
            if (disabled) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            // Handle Enter key
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();

              // Check if autocomplete has a place selected
              const place = autocompleteRef.current?.getPlace();
              const hasPlaceSelected = place && (place.place_id || place.geometry?.location);

              // If autocomplete has a place selected, let handlePlaceSelect handle it
              // Otherwise, geocode the typed address
              if (!hasPlaceSelected && handleGeocodeAddress.current && !isSelectingFromAutocomplete.current) {
                handleGeocodeAddress.current();
              }
            }
            // Stop propagation for all keys to prevent interference with other forms
            e.stopPropagation();
          }}
          onKeyPress={(e) => {
            // If disabled, prevent all keyboard input
            if (disabled) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onKeyUp={(e) => {
            // If disabled, prevent all keyboard input
            if (disabled) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onFocus={() => {
            // If disabled, prevent focus
            if (disabled) {
              if (inputRef.current) {
                inputRef.current.blur();
              }
              return;
            }
            setIsFocused(true);
            onFocus?.();
          }}
          onBlur={(_e) => {
            // Delay blur processing to allow autocomplete selection to complete
            // This is especially important for mouse/touch selection
            // When clicking on autocomplete predictions, blur fires before place_changed
            // Use a longer delay when clicking to ensure place_changed fires first
            const delay = isClickingAutocompleteRef.current ? 800 : 300;
            blurTimeoutRef.current = window.setTimeout(() => {
              // Only process blur if we're not in the middle of selecting from autocomplete
              if (!isSelectingFromAutocomplete.current && !isClickingAutocompleteRef.current) {
                setIsFocused(false);
                onBlur?.();
              } else {
                // Cancel the blur - we're selecting from autocomplete
                // The place_changed event will handle the selection and blur
                console.log('Blur canceled - autocomplete selection in progress, waiting for place_changed');
                // The handlePlaceSelect function will handle blurring after selection completes
              }
            }, delay);
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 py-3 pr-2 text-gray-900 placeholder-gray-500 bg-transparent border-none outline-none text-[15px]"
          autoComplete="off"
          data-form-type="location"
        />

        {/* Current location button */}
        <button
          type="button"
          onClick={handleCurrentLocation}
          disabled={disabled}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed mr-1"
          title="Use current location"
        >
          <span className="material-symbols-outlined text-blue-600 text-[20px]">
            my_location
          </span>
        </button>
      </div>
    </div>
  );
};
